<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TextArea from 'next/textarea/TextArea.vue';

const props = defineProps({
  chat: { type: Object, required: true },
});

const store = useStore();
const { t } = useI18n();
const dialogRef = ref(null);
const operation = ref('');
const reasonId = ref('');
const summary = ref('');
const assigneeId = ref('');
const teamId = ref('');
const justification = ref('');
const isLoading = ref(false);

const reasons = computed(() =>
  store.getters['resolutionReasons/getActiveResolutionReasons'].map(reason => ({
    value: reason.id,
    label: reason.name || reason.label || reason.description,
  }))
);
const agents = computed(() =>
  store.getters['agents/getAgents'].map(agent => ({
    value: agent.id,
    label: agent.name || agent.available_name,
  }))
);
const teams = computed(() =>
  store.getters['teams/getTeams'].map(team => ({
    value: team.id,
    label: team.name,
  }))
);
const title = computed(() => {
  if (operation.value === 'RESOLVE')
    return t('CONVERSATION.OPERATIONS.RESOLVE.TITLE');
  if (operation.value === 'REOPEN')
    return t('CONVERSATION.OPERATIONS.REOPEN.TITLE');
  if (operation.value === 'TRANSFER')
    return t('CONVERSATION.OPERATIONS.TRANSFER.TITLE');
  return t('CONVERSATION.OPERATIONS.RELEASE.TITLE');
});
const confirmLabel = computed(() => {
  if (operation.value === 'RESOLVE')
    return t('CONVERSATION.OPERATIONS.RESOLVE.CONFIRM');
  if (operation.value === 'REOPEN')
    return t('CONVERSATION.OPERATIONS.REOPEN.CONFIRM');
  if (operation.value === 'TRANSFER')
    return t('CONVERSATION.OPERATIONS.TRANSFER.CONFIRM');
  return t('CONVERSATION.OPERATIONS.RELEASE.CONFIRM');
});
const isSubmitDisabled = computed(() => {
  if (operation.value === 'RESOLVE') return !reasonId.value || !summary.value;
  if (operation.value === 'REOPEN') return !justification.value;
  if (operation.value === 'TRANSFER')
    return !assigneeId.value || !justification.value;
  return !justification.value;
});

const reset = () => {
  reasonId.value = '';
  summary.value = '';
  assigneeId.value = '';
  teamId.value = '';
  justification.value = '';
};

const open = type => {
  operation.value = type;
  reset();
  if (type === 'RESOLVE') store.dispatch('resolutionReasons/getActive');
  dialogRef.value?.open();
};

const getLastMessageId = () => {
  const messages = props.chat.messages || [];
  return messages.at(-1)?.id || props.chat.last_message?.id || null;
};

const getErrorMessage = code => {
  if (code === 'conversation_changed') {
    return t('CONVERSATION.OPERATIONS.CONVERSATION_CHANGED');
  }
  if (code === 'validation_error') {
    return t('CONVERSATION.OPERATIONS.VALIDATION_ERROR');
  }
  if (operation.value === 'RESOLVE')
    return t('CONVERSATION.OPERATIONS.RESOLVE.ERROR');
  if (operation.value === 'REOPEN')
    return t('CONVERSATION.OPERATIONS.REOPEN.ERROR');
  if (operation.value === 'TRANSFER')
    return t('CONVERSATION.OPERATIONS.TRANSFER.ERROR');
  return t('CONVERSATION.OPERATIONS.RELEASE.ERROR');
};

const showSuccessAlert = () => {
  if (operation.value === 'RESOLVE') {
    useAlert(t('CONVERSATION.OPERATIONS.RESOLVE.SUCCESS'));
  } else if (operation.value === 'REOPEN') {
    useAlert(t('CONVERSATION.OPERATIONS.REOPEN.SUCCESS'));
  } else if (operation.value === 'TRANSFER') {
    useAlert(t('CONVERSATION.OPERATIONS.TRANSFER.SUCCESS'));
  } else {
    useAlert(t('CONVERSATION.OPERATIONS.RELEASE.SUCCESS'));
  }
};

const submit = async () => {
  isLoading.value = true;
  try {
    if (operation.value === 'RESOLVE') {
      await store.dispatch('resolveConversation', {
        conversationId: props.chat.id,
        reasonId: reasonId.value,
        summary: summary.value,
        expectedLastMessageId: getLastMessageId(),
      });
    } else if (operation.value === 'REOPEN') {
      await store.dispatch('reopenConversation', {
        conversationId: props.chat.id,
        justification: justification.value,
      });
    } else if (operation.value === 'TRANSFER') {
      await store.dispatch('transferConversation', {
        conversationId: props.chat.id,
        assigneeId: assigneeId.value,
        teamId: teamId.value || null,
        justification: justification.value,
      });
    } else {
      await store.dispatch('releaseConversation', {
        conversationId: props.chat.id,
        justification: justification.value,
      });
    }
    showSuccessAlert();
    dialogRef.value?.close();
  } catch (error) {
    const code = error.response?.data?.code;
    useAlert(getErrorMessage(code));
  } finally {
    isLoading.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :title="title"
    :confirm-button-label="confirmLabel"
    :disable-confirm-button="isSubmitDisabled"
    :is-loading="isLoading"
    @confirm="submit"
  >
    <div class="flex flex-col gap-4">
      <template v-if="operation === 'RESOLVE'">
        <ComboBox
          v-model="reasonId"
          :options="reasons"
          :placeholder="t('CONVERSATION.OPERATIONS.RESOLVE.REASON')"
          :search-placeholder="t('CONVERSATION.OPERATIONS.SEARCH')"
          :empty-state="t('CONVERSATION.OPERATIONS.NO_REASONS')"
        />
        <TextArea
          v-model="summary"
          :label="t('CONVERSATION.OPERATIONS.RESOLVE.SUMMARY')"
          :placeholder="
            t('CONVERSATION.OPERATIONS.RESOLVE.SUMMARY_PLACEHOLDER')
          "
          :max-length="2000"
          show-character-count
          resize
        />
      </template>
      <template v-else-if="operation === 'REOPEN'">
        <TextArea
          v-model="justification"
          :label="t('CONVERSATION.OPERATIONS.JUSTIFICATION')"
          :max-length="1000"
          show-character-count
          resize
        />
      </template>
      <template v-else-if="operation === 'TRANSFER'">
        <ComboBox
          v-model="assigneeId"
          :options="agents"
          :placeholder="t('CONVERSATION.OPERATIONS.TRANSFER.ASSIGNEE')"
          :search-placeholder="t('CONVERSATION.OPERATIONS.SEARCH')"
          :empty-state="t('CONVERSATION.OPERATIONS.NO_AGENTS')"
        />
        <ComboBox
          v-model="teamId"
          :options="teams"
          :placeholder="t('CONVERSATION.OPERATIONS.TRANSFER.TEAM')"
          :search-placeholder="t('CONVERSATION.OPERATIONS.SEARCH')"
          :empty-state="t('CONVERSATION.OPERATIONS.NO_TEAMS')"
        />
        <TextArea
          v-model="justification"
          :label="t('CONVERSATION.OPERATIONS.JUSTIFICATION')"
          :max-length="1000"
          show-character-count
          resize
        />
      </template>
      <template v-else>
        <TextArea
          v-model="justification"
          :label="t('CONVERSATION.OPERATIONS.JUSTIFICATION')"
          :max-length="1000"
          show-character-count
          resize
        />
      </template>
    </div>
  </Dialog>
</template>
